<template>
  <div class="fish-scene" :class="[`fish-scene--${fase}`]">
    <div class="fish-scene__cenario" aria-hidden="true">
      <div
        v-for="n in camadas"
        :key="n"
        class="fish-scene__plano"
        :class="{ 'fish-scene__plano--nada': ehNadador(n) }"
        :style="estiloDoPlano(n)"
      />
      <!-- A camada de escurecimento, ENTRE o cenário e a UI (D8). Ver o comentário no script. -->
      <div class="fish-scene__veu" :style="{ opacity: escurecimento }" />
    </div>

    <div class="fish-scene__conteudo">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * 2.10 — `FishScene`: o cenário em parallax. **Cada tela é um lugar, não uma página** (D8, D10-1).
 *
 * ## O diagnóstico que este componente responde
 *
 * > Um aplicativo apresenta dados; **um jogo apresenta um lugar.** Hoje as telas do Fishbay são
 * > listas de cards sobre um gradiente liso. Nada está em lugar nenhum, nada se move.
 *
 * O fundo em parallax faz o jogador **saber onde está antes de ler o título** — e é a primeira das
 * quatro mudanças do D10 justamente porque é a de maior alcance: ela vale para toda tela de uma vez.
 *
 * ## Como o parallax funciona aqui, e por que não segue o mouse
 *
 * As camadas andam **sozinhas**, em velocidades diferentes, num vaivém lento de 6 a 30 segundos. Não
 * há reação a mouse nem a giroscópio, e isso é decisão do D0: parallax que segue o ponteiro exige
 * que o jogador se mexa para a tela ficar viva, e transforma o cenário numa coisa a ser operada. A
 * água se move porque é água.
 *
 * A velocidade é proporcional à profundidade: `plano-1` (o fundo) quase não anda, o último anda
 * mais. É a inversão do que a intuição diz e o que a paralaxe de verdade faz — o que está perto
 * passa rápido.
 *
 * ## A regra que impede o cenário de atrapalhar (D8)
 *
 * > Camada de escurecimento entre o fundo e a UI, e as camadas da frente ficam **atrás** do
 * > conteúdo, nunca por cima. **Cenário é lugar, não decoração sobreposta.**
 *
 * Está no CSS: o `__cenario` inteiro é `position: absolute` com `z-index: var(--z-cenario)` (0) e o
 * `__conteudo` é `--z-conteudo` (1). Não há caminho em que uma camada suba por cima do conteúdo, e
 * `pointer-events: none` garante que nenhuma delas roube um toque.
 *
 * ## A escala 3× (D8)
 *
 * As cenas são 576×324 nativas. `background-size: cover` com `image-rendering: pixelated` cobre a
 * tela sem borrar. **Escalar por valor não inteiro reintroduz a interpolação do UI-11** — por isso
 * `cover` e não `100% 100%`, que esticaria os dois eixos por fatores diferentes.
 *
 * ## A `fase` (D27, 10.1)
 *
 * A hora do dia entra como **tinta sobre as mesmas camadas**: *"dá sete rostos ao mesmo mapa sem um
 * asset novo."* Está pronta aqui porque é barata e porque o `FishScene` é o único lugar que
 * precisaria saber disso — quando a 10.1 chegar, ela liga a prop e não mexe em mais nada.
 */

/** Quantas camadas cada cena tem. Medido pelo `recortar-cenarios.mjs` — ver o README de lá. */
const CAMADAS_POR_CENA = {
  'recife-raso': 6,
  'recife-profundo': 4,
  naufragio: 4,
  'mar-aberto': 5,
  'agua-doce': 5,
  praia: 5,
  abissal: 6,
  gruta: 4,
  // Os dois últimos mapas são de CÉU, não de água (pack de nuvens da craftpix, 03/09/2026).
  'ilhas-cazu': 5,
  'costa-dourada': 3,
  vila: 4, // a enseada com nuvens (Ocean_3) — a tela das seis lojas
};

/**
 * As camadas que são CRIATURA, e não cenário (autor, 03/09: "não tem como fazer algo como se os
 * peixes estivessem nadando? esse anda pra lá e pra cá ficou bem feio").
 *
 * Coral balança no vaivém e parece corrente; peixe no vaivém parece pêndulo — peixe não dá ré.
 * Estas camadas trocam o vaivém por um DESLIZE contínuo numa direção só, com `repeat-x`: o cardume
 * atravessa, sai, e volta a entrar. Conferido camada a camada olhando os PNGs: em todas elas os
 * peixes olham para a ESQUERDA, então o deslize é para a esquerda.
 */
const NADADORES_POR_CENA = {
  'recife-raso': [6],
  'recife-profundo': [4],
  'mar-aberto': [5],
  'agua-doce': [2],
};

export default {
  name: 'FishScene',
  props: {
    /** O nome da cena, sem o `cena-`. Ver a distribuição por contexto no D8. */
    cena: {
      type: String,
      default: 'recife-raso',
      validator: (v) => v in CAMADAS_POR_CENA,
    },
    /**
     * Quanto o cenário recua para o conteúdo aparecer.
     *
     * Tela de leitura (enciclopédia, livro-caixa) precisa de mais; tela de pescar, de menos —
     * ali o cenário É o conteúdo. O padrão serve à maioria.
     */
    escurecimento: { type: Number, default: 0.55 },
    /** A fase do dia (D27). `dia` até a 10.1 existir. */
    fase: {
      type: String,
      default: 'dia',
      validator: (v) => ['amanhecer', 'dia', 'entardecer', 'noite'].includes(v),
    },
  },
  computed: {
    camadas() {
      return CAMADAS_POR_CENA[this.cena] || 4;
    },
  },
  methods: {
    ehNadador(n) {
      return (NADADORES_POR_CENA[this.cena] || []).includes(n);
    },
    estiloDoPlano(n) {
      const total = this.camadas;
      // O plano 1 é o fundo (fator ~0) e o último é o da frente (fator 1).
      const profundidade = (n - 1) / Math.max(1, total - 1);

      // A camada que NADA: um tile de 1728px (o 3× nativo) deslizando inteiro por ciclo — a
      // viagem é exatamente uma largura de tile, então o laço emenda sem pulo.
      if (this.ehNadador(n)) {
        return {
          backgroundImage: `url('/images/cenarios/cena-${this.cena}/plano-${n}.png')`,
          zIndex: n,
          animationDuration: '85s',
          animationDelay: `${-n * 11}s`,
        };
      }

      return {
        backgroundImage: `url('/images/cenarios/cena-${this.cena}/plano-${n}.png')`,
        zIndex: n,
        // 30s no fundo, 6s na frente: o de trás quase não anda.
        animationDuration: `${30 - profundidade * 24}s`,
        // Cada camada começa em um ponto diferente do ciclo. Sem isso as camadas andam em bloco e o
        // parallax some — elas se moveriam juntas, que é o mesmo que uma imagem só se movendo.
        animationDelay: `${-n * 2.5}s`,
        // A amplitude cresce com a proximidade. `--anda` é lida pelos keyframes.
        '--anda': `${0.5 + profundidade * 2.5}%`,
      };
    },
  },
};
</script>

<style scoped>
.fish-scene {
  position: relative;
  min-height: 100%;
  isolation: isolate; /* prende as camadas neste contexto; nenhum z-index daqui vaza para a página */
}

.fish-scene__cenario {
  position: absolute;
  inset: 0;
  z-index: var(--z-cenario);
  overflow: hidden;
  pointer-events: none; /* o cenário nunca rouba um toque do conteúdo */
  background: var(--agua-abissal);
}

.fish-scene__plano {
  position: absolute;
  /* As camadas são maiores que a caixa e ficam centradas: sem a folga, o vaivém do parallax
   * mostraria a borda da imagem no fim de cada curso. */
  inset: -4% -6%;
  background-position: center bottom;
  background-size: cover;
  background-repeat: no-repeat;
  image-rendering: pixelated;
  animation-name: cenario-anda;
  animation-timing-function: var(--curva-agua);
  animation-iteration-count: infinite;
}

@keyframes cenario-anda {
  0%,
  100% {
    transform: translateX(calc(var(--anda) * -1));
  }
  50% {
    transform: translateX(var(--anda));
  }
}

/**
 * A camada que NADA (peixes, criaturas): deslize contínuo para a esquerda com `repeat-x`, no
 * tamanho FIXO de 3× (1728×972) para o laço fechar exato — cover deixaria a largura do tile ao
 * sabor da janela e o fim do ciclo pularia. Ancorada embaixo: numa tela mais alta que 972px a
 * faixa de cima fica sem esta camada, e tudo bem — peixe não mora no teto.
 */
.fish-scene__plano--nada {
  background-size: 1728px 972px;
  background-repeat: repeat-x;
  background-position: 0 bottom;
  animation-name: cenario-nada;
  animation-timing-function: linear;
}

@keyframes cenario-nada {
  from {
    background-position-x: 0;
  }
  to {
    background-position-x: -1728px;
  }
}

.fish-scene__veu {
  position: absolute;
  inset: 0;
  z-index: 90; /* acima de qualquer plano (o máximo é 6), abaixo do conteúdo */
  background: linear-gradient(to bottom, var(--agua-abissal), rgba(14, 27, 38, 0.7) 40%, var(--agua-abissal));
}

.fish-scene__conteudo {
  position: relative;
  z-index: var(--z-conteudo);
  /* "Descer na água ao entrar" (a nota de movimento do documento, §642): toda tela com cenário
   * entra afundando de leve. Uma vez, curto, e para — transição, não enfeite (D0).
   *
   * O afundar é por `top`, e NÃO por `transform`. Um `transform` — mesmo o `translateY(0)` que o
   * `both` deixa aplicado para sempre — vira o bloco contentor de todo `position: fixed` que
   * estiver dentro: a ação fixa da Home (`.cais__acao`) passava a se posicionar em relação a esta
   * caixa em vez da janela. `top` num `position: relative` não tem esse efeito. */
  animation: fish-scene-mergulhar var(--t-lento, 0.4s) var(--curva-agua) both;
}

@keyframes fish-scene-mergulhar {
  from {
    opacity: 0;
    top: -14px;
  }
  to {
    opacity: 1;
    top: 0;
  }
}

/**
 * A fase do dia (D27): a MESMA arte com outra luz.
 *
 * É `filter` sobre o cenário e só sobre ele — o conteúdo fica de fora, senão o texto mudaria de cor
 * ao anoitecer. Este é um dos poucos filtros em runtime que o redesenho aceita, e vale porque a
 * alternativa seria guardar 39 camadas × 4 fases = 156 arquivos para o que é uma matiz.
 */
.fish-scene--amanhecer .fish-scene__cenario {
  filter: sepia(0.25) hue-rotate(-12deg) brightness(1.05) saturate(0.9);
}

.fish-scene--entardecer .fish-scene__cenario {
  filter: sepia(0.35) hue-rotate(-20deg) brightness(0.92) saturate(1.05);
}

.fish-scene--noite .fish-scene__cenario {
  filter: brightness(0.55) saturate(0.75) hue-rotate(12deg);
}

/* O cenário PARA de se mover, mas continua lá: ele é o lugar, não a animação (UI-25). */
@media (prefers-reduced-motion: reduce) {
  .fish-scene__plano,
  .fish-scene__conteudo {
    animation: none;
  }
}
</style>
